import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

function Admins() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchAdmins(); }, []);

  const fetchAdmins = async () => {
    try {
      const { data } = await api.get("/users");
      setAdmins(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === user.id) {
      alert("Vous ne pouvez pas supprimer votre propre compte !");
      return;
    }
    if (admins.length <= 1) {
      alert("Impossible de supprimer le dernier administrateur !");
      return;
    }
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet administrateur ?")) return;
    try {
      await api.delete(`/users/${id}`);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const filtered = admins.filter((a) =>
    `${a.prenom} ${a.nom}`.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #37474f 0%, #455a64 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(55,71,79,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Gestion des Administrateurs
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {admins.length} administrateur{admins.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                <MDButton variant="contained" size="medium"
                  onClick={() => navigate("/admins/nouveau")}
                  sx={{
                    background: "#fff", color: "#37474f",
                    fontWeight: 700, borderRadius: "10px",
                    "&:hover": { background: "#eceff1" },
                  }}>
                  <Icon sx={{ mr: 0.5 }}>add</Icon> Ajouter un admin
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>

                {/* Recherche */}
                <MDBox mb={3} sx={{ maxWidth: 400 }}>
                  <MDInput fullWidth
                    label="🔍  Rechercher par nom ou email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>admin_panel_settings</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun administrateur trouvé
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {["Administrateur", "Email", "Compte actuel", "Actions"].map((h) => (
                            <th key={h} style={{
                              padding: "10px 16px", textAlign: "left",
                              fontSize: 11, fontWeight: 700, color: "#9e9e9e",
                              textTransform: "uppercase", letterSpacing: "0.05em",
                              borderBottom: "2px solid #f5f5f5",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((admin) => (
                          <tr key={admin.id}
                            style={{ borderBottom: "1px solid #fafafa" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fa")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" alignItems="center" gap={1.5}>
                                <MDBox sx={{
                                  width: 38, height: 38, borderRadius: "50%",
                                  background: "linear-gradient(135deg, #37474f, #455a64)",
                                  color: "#fff", display: "flex", alignItems: "center",
                                  justifyContent: "center", fontWeight: 700, fontSize: 14,
                                }}>
                                  {(admin.prenom?.[0] || "")}{(admin.nom?.[0] || "")}
                                </MDBox>
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                  {admin.prenom} {admin.nom}
                                </MDTypography>
                              </MDBox>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {admin.email}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              {admin.id === user.id && (
                                <MDBox sx={{
                                  display: "inline-block", padding: "3px 12px",
                                  borderRadius: "12px", background: "#e8f5e9",
                                  color: "#2e7d32", fontSize: 12, fontWeight: 600,
                                }}>
                                  ✓ Vous
                                </MDBox>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" gap={1}>
                                <Tooltip title="Modifier">
                                  <MDBox onClick={() => navigate(`/admins/${admin.id}/modifier`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#fff8e1", color: "#f57f17",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffecb3" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                  </MDBox>
                                </Tooltip>
                                {admin.id !== user.id && (
                                  <Tooltip title="Supprimer">
                                    <MDBox onClick={() => handleDelete(admin.id)} sx={{
                                      width: 32, height: 32, borderRadius: "8px",
                                      background: "#ffebee", color: "#c62828",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                    }}>
                                      <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                                    </MDBox>
                                  </Tooltip>
                                )}
                              </MDBox>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Admins;